import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  type ColumnDef,
  getCoreRowModel,
  getFilteredRowModel,
  useReactTable,
} from "@tanstack/react-table";

import InsuranceAttributeCard from "@/components/InsuranceAttributeCard";
import LearnerPortalShell from "@/components/learner/LearnerPortalShell";
import RuleDiffAttributeCard from "@/components/RuleDiffAttributeCard";
import {
  getPerStateInsurance,
  INSURANCE_ATTR_DISPLAY_ORDER,
} from "@/data/per-state-insurance";
import { useTrialStatus } from "@/hooks/useTrialStatus";
import {
  ApiError,
} from "@/lib/api";
import {
  type EntitlementTier,
  listMyEntitlements,
} from "@/lib/entitlement-api";
import { getProfile, type UserProfile } from "@/lib/profile-api";
import {
  getBrowseCountry,
  isoToBrowseCode,
} from "@/lib/rulediff-browse-countries";
import {
  getBrowseState,
} from "@/lib/rulediff-browse-states";
import {
  ATTR_DISPLAY_ORDER,
  type RuleDiffSeedEntry,
  formatAttrValue,
  getSeedRuleDiff,
} from "@/lib/rulediff-seed-api";

/**
 * apps/web :: pages :: RuleDiffBrowsePage
 *
 * Authenticated-only browse surface for the precomputed rule-diff seed
 * corpus (1,737 country-state JSONs from Wave 17b/17c). Calls the
 * Wave 17d PR-A backend (`GET /api/rule-diff/seed/{country}/{state}`).
 *
 * Distinct from `RuleDiffPreviewPage` (the LLM-generate playground):
 *   - Browse: country/state picker → render 10 structured attribute
 *     cards instantly. Zero LLM cost. Severity-tagged colors.
 *   - Preview: free-text form → LLM-generated prose diff. Costs
 *     tokens; ~2-5 sec latency.
 *
 * Scoped to the user (ROADS-567):
 *   - Home country is LOCKED to `profile.home_country` (mapped via
 *     {@link isoToBrowseCode}) — rendered as a read-only display, not a
 *     picker. The learner can only browse diffs *from* their own home
 *     country. Falls back to the raw code if it isn't in the browse
 *     catalog (still locked).
 *   - Target state is limited to the union of the user's BOUGHT states
 *     (active, state-scoped entitlements) and, when a trial is active,
 *     their TRIALING states (`profile.target_states`). Defaults to
 *     `profile.current_focus_state` when it's in that set. Empty set ⇒
 *     fall back to the focus state so one state always renders.
 *
 * Load-state machine mirrors the established DashboardPage pattern:
 * loading → ready / not-found / error. 404 from the seed endpoint is a
 * legitimate "not yet supported pair" outcome (e.g., a country/state
 * outside the corpus) and is rendered as a warm empty state, not an
 * error toast.
 */

type LoadState =
  | { kind: "idle" }
  | { kind: "loading" }
  | { kind: "ready"; entries: RuleDiffSeedEntry[] }
  | { kind: "not-found" }
  | { kind: "error"; message: string };

/** State-scoped paid tiers — the ones that carry a non-null `state_code`. */
const STATE_SCOPED_TIERS: ReadonlySet<EntitlementTier> = new Set<EntitlementTier>([
  "class_c",
  "cdl",
  "motorcycle",
]);

export default function RuleDiffBrowsePage() {
  const { t } = useTranslation();
  const trial = useTrialStatus();

  const [profile, setProfile] = useState<UserProfile | null>(null);
  // Bought = state_codes of active, state-scoped entitlements. Held in
  // lowercase browse-code form. Empty until the entitlements probe lands.
  const [boughtStates, setBoughtStates] = useState<string[]>([]);

  const [state, setState] = useState<string>("");
  const [loadState, setLoadState] = useState<LoadState>({ kind: "idle" });

  // Load profile + entitlements once. Best-effort; swallow failures
  // silently because the page must still render a usable (locked) state.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const p = await getProfile();
        if (cancelled) return;
        setProfile(p);
      } catch {
        // Profile unavailable — country falls back to the raw/unknown
        // display, state derivation degrades gracefully below.
      }
    })();
    void (async () => {
      try {
        const res = await listMyEntitlements();
        if (cancelled) return;
        const now = res.now_ms;
        const codes = res.entitlements
          .filter(
            (e) =>
              STATE_SCOPED_TIERS.has(e.tier) &&
              e.state_code != null &&
              e.expires_at_ms > now,
          )
          .map((e) => (e.state_code as string).toLowerCase());
        setBoughtStates(codes);
      } catch {
        // No entitlements readable — bought set stays empty; trial /
        // focus-state fallbacks still apply.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Locked home country, resolved from the profile. Display name comes
  // from the browse catalog; if home_country isn't in the catalog we
  // still show the (upper-cased) raw code — locked either way.
  const homeBrowseCode = isoToBrowseCode(profile?.home_country);
  const country = homeBrowseCode ?? "";
  const countryName =
    (homeBrowseCode ? getBrowseCountry(homeBrowseCode)?.name : undefined) ??
    profile?.home_country?.toUpperCase() ??
    "";

  // Allowed target states = unique union of bought + trialing states,
  // intersected with the browse catalog. Trialing states are the
  // profile's target_states, included only while a trial is active.
  const allowedStates = useMemo(() => {
    const trialActive = trial.kind === "ready" && trial.status.active;
    const trialing =
      trialActive && profile ? (profile.target_states ?? []) : [];
    const union = [...boughtStates, ...trialing.map((s) => s.toLowerCase())];
    // De-dupe and keep only codes the browse catalog knows about.
    const seen = new Set<string>();
    const out: string[] = [];
    for (const code of union) {
      const known = getBrowseState(code);
      if (known && !seen.has(known.code)) {
        seen.add(known.code);
        out.push(known.code);
      }
    }
    return out;
  }, [boughtStates, trial, profile]);

  // The actual option set rendered by the picker. Empty allowed set
  // (no entitlements, no active trial) ⇒ fall back to a single state so
  // the page renders one option rather than an empty picker:
  // current_focus_state if known, else target_states[0].
  const stateOptions = useMemo(() => {
    if (allowedStates.length > 0) return allowedStates;
    const fallbackRaw =
      profile?.current_focus_state ?? profile?.target_states?.[0];
    const known = fallbackRaw ? getBrowseState(fallbackRaw) : undefined;
    return known ? [known.code] : [];
  }, [allowedStates, profile]);

  // Keep the selected state inside the allowed set. Default to the
  // profile's focus state when it's allowed, else the first option.
  useEffect(() => {
    if (stateOptions.length === 0) return;
    const focus = profile?.current_focus_state?.toLowerCase();
    const preferred =
      focus && stateOptions.includes(focus) ? focus : stateOptions[0];
    setState((current) =>
      stateOptions.includes(current) ? current : preferred,
    );
  }, [stateOptions, profile]);

  // Fetch seed whenever the picker changes. Cancellation guard for
  // out-of-order responses if the user changes selection mid-flight.
  useEffect(() => {
    // Wait until the locked country + a state are resolved before
    // hitting the seed endpoint (avoids a spurious 404 on first paint).
    if (!country || !state) return;
    let cancelled = false;
    setLoadState({ kind: "loading" });
    void (async () => {
      try {
        const entries = await getSeedRuleDiff(country, state);
        if (cancelled) return;
        // Sort entries into the canonical display order.
        const ordered = [...entries].sort(
          (a, b) =>
            ATTR_DISPLAY_ORDER.indexOf(a.attr_name) -
            ATTR_DISPLAY_ORDER.indexOf(b.attr_name),
        );
        setLoadState({ kind: "ready", entries: ordered });
      } catch (err) {
        if (cancelled) return;
        if (err instanceof ApiError && err.status === 404) {
          setLoadState({ kind: "not-found" });
          return;
        }
        const message =
          err instanceof Error ? err.message : t("ruleDiffBrowse.errorGeneric");
        setLoadState({ kind: "error", message });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [country, state, t]);

  return (
    <LearnerPortalShell pageTitle={t("ruleDiffBrowse.title")}>
      <section className="mx-auto max-w-4xl px-md pt-xl pb-xl sm:px-lg sm:pt-2xl sm:pb-2xl">
        <header className="max-w-2xl">
        <p className="text-sm font-semibold uppercase tracking-wide text-neutral-600">
          {t("ruleDiffBrowse.eyebrow")}
        </p>
        <h1 className="mt-sm font-display text-3xl font-bold tracking-tight text-brand-primary sm:text-4xl">
          {t("ruleDiffBrowse.title")}
        </h1>
        <div
          className="mt-md h-1 w-16 rounded-full bg-brand-accent"
          aria-hidden="true"
        />
        <p className="mt-md text-base text-neutral-800">
          {t("ruleDiffBrowse.subtitle")}
        </p>
      </header>

      <fieldset
        className="mt-xl rounded-md border border-neutral-200 bg-white p-md sm:p-lg"
      >
        <legend className="px-sm text-sm font-semibold text-neutral-800">
          {t("ruleDiffBrowse.pickerLegend")}
        </legend>
        <div className="grid grid-cols-1 gap-md sm:grid-cols-2">
          <label className="flex flex-col gap-xs">
            <span className="text-sm font-medium text-neutral-800">
              {t("ruleDiffBrowse.countryLabel")}
            </span>
            {/*
              Home country is LOCKED to the profile (ROADS-567). Rendered
              as a read-only display rather than a picker so the learner
              can only browse diffs from their own home country. The
              data-testid is preserved on the element for existing
              selectors.
            */}
            <output
              data-testid="rule-diff-browse-country"
              data-country-code={country}
              className="rounded-md border border-neutral-200 bg-neutral-50 px-sm py-xs text-base text-neutral-800"
            >
              {countryName}
              {homeBrowseCode && getBrowseCountry(homeBrowseCode)?.isLhs
                ? ` ${t("ruleDiffBrowse.lhsTag")}`
                : ""}
            </output>
          </label>
          <label className="flex flex-col gap-xs">
            <span className="text-sm font-medium text-neutral-800">
              {t("ruleDiffBrowse.stateLabel")}
            </span>
            {/*
              Target state limited to the user's entitled (bought) +
              trialing states (ROADS-567). Flat list — no optgroup-by-
              category — because the allowed set is small and per-user.
            */}
            <select
              data-testid="rule-diff-browse-state"
              value={state}
              onChange={(e) => setState(e.target.value)}
              className="rounded-md border border-neutral-300 bg-white px-sm py-xs text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
            >
              {stateOptions.map((code) => {
                const s = getBrowseState(code);
                if (!s) return null;
                return (
                  <option key={s.code} value={s.code}>
                    {s.name}
                    {s.isLhs ? ` ${t("ruleDiffBrowse.lhsTag")}` : ""}
                  </option>
                );
              })}
            </select>
          </label>
        </div>
      </fieldset>

      <div className="mt-xl" data-testid="rule-diff-browse-results">
        {loadState.kind === "loading" && (
          <p
            role="status"
            aria-live="polite"
            className="text-sm text-neutral-600"
          >
            {t("ruleDiffBrowse.loading")}
          </p>
        )}
        {loadState.kind === "not-found" && (
          <div
            role="status"
            className="rounded-md border border-amber-300 bg-amber-50 p-md text-sm text-amber-900"
          >
            <p className="font-semibold">
              {t("ruleDiffBrowse.notFoundTitle")}
            </p>
            <p className="mt-xs">{t("ruleDiffBrowse.notFoundBody")}</p>
          </div>
        )}
        {loadState.kind === "error" && (
          <div
            role="alert"
            className="rounded-md border border-rose-300 bg-rose-50 p-md text-sm text-rose-900"
          >
            <p className="font-semibold">{t("ruleDiffBrowse.errorTitle")}</p>
            <p className="mt-xs">{loadState.message}</p>
          </div>
        )}
        {loadState.kind === "ready" && (
          <RuleDiffResults entries={loadState.entries} />
        )}
      </div>

      {/*
        Insurance section. Renders for all 50 + DC; surfaces a "not
        available" notice for territories. Independent of the rule-diff
        load state — insurance content is a per-state lookup, not a
        country×state pair, so we can render it as long as the user has
        a state selected.
      */}
      <InsuranceSection stateCode={state} />
      </section>
    </LearnerPortalShell>
  );
}

/**
 * Filterable rule-diff card list. Per ADR-0014, learner-side surfaces
 * adopt TanStack headless primitives directly — we use `useReactTable`
 * for its global-filter state machine and render the matched rows as a
 * card `<ul>`. No `<table>` markup is introduced.
 *
 * Filter scope (joined into one searchable string per row):
 *   - The localized attribute heading (e.g. "School Bus Stop Law")
 *   - The formatted attribute value (e.g. "Must Stop Both Directions")
 *   - The free-text `reviewer_notes` (the SME context block)
 *
 * For learners reading rule-diff content in a non-native language the
 * filter is a real affordance — they can search by an English keyword
 * the SME used in their notes even when the attribute heading is
 * localized to their UI language.
 *
 * NOTE: today the page renders exactly 10 cards per (country, state)
 * pair, so the marginal value of TanStack vs. a hand-rolled
 * `useState`+`filter()` is small. We chose TanStack anyway so the
 * filter logic + empty-state behaviour stays consistent with the rest
 * of the codebase's table chrome (ROADS-478, ROADS-479). If the corpus
 * later expands to cross-country / multi-pair browse, this hook is
 * ready to grow into sort / facet UIs without a rewrite.
 */
function RuleDiffResults({ entries }: { entries: RuleDiffSeedEntry[] }) {
  const { t } = useTranslation();
  const [globalFilter, setGlobalFilter] = useState("");

  const columns = useMemo<ColumnDef<RuleDiffSeedEntry, unknown>[]>(
    () => [
      {
        id: "searchable",
        // Project every searchable surface into one string per row so
        // TanStack's default global-filter can match across all of them.
        accessorFn: (row) => {
          const headingKey = `ruleDiffBrowse.attr.${row.attr_name}`;
          const localizedHeading = t(headingKey, {
            defaultValue: row.attr_name,
          });
          return [
            localizedHeading,
            formatAttrValue(row),
            row.reviewer_notes,
          ]
            .filter(Boolean)
            .join(" • ");
        },
      },
    ],
    [t],
  );

  const table = useReactTable({
    data: entries,
    columns,
    state: { globalFilter },
    onGlobalFilterChange: setGlobalFilter,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    getRowId: (row) => row.attr_name,
  });

  const visibleRows = table.getRowModel().rows;

  return (
    <div>
      <label className="block">
        <span className="sr-only">{t("ruleDiffBrowse.searchLabel")}</span>
        <input
          type="search"
          data-testid="rule-diff-browse-search"
          value={globalFilter}
          onChange={(e) => setGlobalFilter(e.target.value)}
          placeholder={t("ruleDiffBrowse.searchPlaceholder")}
          className="w-full max-w-md rounded-md border border-neutral-300 bg-white px-sm py-xs text-base focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
        />
      </label>
      {visibleRows.length === 0 ? (
        <div
          role="status"
          data-testid="rule-diff-browse-filter-empty"
          className="mt-md rounded-md border border-neutral-200 bg-neutral-50 p-md text-sm text-neutral-700"
        >
          {t("ruleDiffBrowse.filterEmpty")}
        </div>
      ) : (
        <ul
          className="mt-md flex flex-col gap-md"
          aria-label={t("ruleDiffBrowse.resultsAriaLabel")}
        >
          {visibleRows.map((row) => (
            <li key={row.id}>
              <RuleDiffAttributeCard entry={row.original} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

interface InsuranceSectionProps {
  /** Lower-case 2-letter state/territory code from the picker. */
  readonly stateCode: string;
}

/**
 * Renders the per-state insurance attribute cards as a sibling section
 * to the rule-diff cards. Unlike the rule-diff cards (one per country
 * pair), insurance content is a state-only lookup — no diff, just the
 * state's published minimums.
 *
 * Layout: section heading + 6 cards (one per insurance attribute,
 * displayed in INSURANCE_ATTR_DISPLAY_ORDER). The 7th attribute
 * (penalty text) renders as a callout block below the grid because
 * it's a paragraph, not a value/citation pair.
 */
function InsuranceSection({ stateCode }: InsuranceSectionProps) {
  const { t } = useTranslation();
  const entry = getPerStateInsurance(stateCode);

  if (!entry) {
    return (
      <section
        aria-labelledby="rule-diff-insurance-section-heading"
        className="mt-2xl"
      >
        <h2
          id="rule-diff-insurance-section-heading"
          className="font-display text-2xl font-bold tracking-tight text-brand-primary sm:text-3xl"
        >
          {t("ruleDiffBrowse.insurance.sectionHeading")}
        </h2>
        <div
          role="status"
          className="mt-md rounded-md border border-amber-300 bg-amber-50 p-md text-sm text-amber-900"
        >
          <p className="font-semibold">
            {t("ruleDiffBrowse.insurance.noEntryTitle")}
          </p>
          <p className="mt-xs">
            {t("ruleDiffBrowse.insurance.noEntryBody")}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section
      aria-labelledby="rule-diff-insurance-section-heading"
      className="mt-2xl"
      data-testid="rule-diff-insurance-section"
    >
      <h2
        id="rule-diff-insurance-section-heading"
        className="font-display text-2xl font-bold tracking-tight text-brand-primary sm:text-3xl"
      >
        {t("ruleDiffBrowse.insurance.sectionHeading")}
      </h2>
      <p className="mt-sm text-base text-neutral-800">
        {t("ruleDiffBrowse.insurance.sectionSubtitle")}
      </p>
      <ul
        className="mt-lg flex flex-col gap-md"
        aria-label={t("ruleDiffBrowse.insurance.sectionAriaLabel")}
      >
        {INSURANCE_ATTR_DISPLAY_ORDER.map((attr) => (
          <li key={attr}>
            <InsuranceAttributeCard entry={entry} attr={attr} />
          </li>
        ))}
      </ul>

      {/*
        Penalty text block. Rendered as a callout, not a card, because
        the content is a multi-sentence paragraph rather than a single
        value. Citation footer mirrors the cards above for consistency.
      */}
      <div
        className="mt-md rounded-md border-l-4 border-rose-300 bg-rose-50/40 p-md sm:p-lg"
        data-testid="rule-diff-insurance-penalty"
      >
        <h3
          id="rule-diff-insurance-penalty-heading"
          className="font-display text-lg font-semibold text-brand-primary"
        >
          {/*
            We reuse the attr-name as the heading so the i18n key
            structure stays simple. The penalty text itself is a
            LocalizedString.
          */}
          {/* No dedicated heading translation — the penalty text is
              self-describing. Use a brief generic heading. */}
          {t("ruleDiffBrowse.insurance.attr.uninsuredMotorist")}
        </h3>
        <p className="mt-sm text-sm text-neutral-800">
          {/*
            Penalty text is a LocalizedString; pull en/es by
            current i18n language. Falls back to en for any locale
            we haven't shipped yet.
          */}
          <PenaltyParagraph entry={entry} />
        </p>
        <footer className="mt-md flex flex-wrap items-center gap-md text-xs text-neutral-600">
          <span>
            {t("ruleDiffBrowse.insurance.retrievedLabel")}:{" "}
            {entry.uninsuredPenaltyCitation.retrievedOn}
          </span>
          <a
            href={entry.uninsuredPenaltyCitation.url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-brand-primary underline-offset-2 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
          >
            {t("ruleDiffBrowse.sourceLink")}
            <span className="sr-only"> {t("ruleDiffBrowse.opensInNewTab")}</span>
          </a>
        </footer>
      </div>
    </section>
  );
}

interface PenaltyParagraphProps {
  readonly entry: ReturnType<typeof getPerStateInsurance>;
}

function PenaltyParagraph({ entry }: PenaltyParagraphProps) {
  const { i18n } = useTranslation();
  if (!entry) return null;
  const text = i18n.language?.startsWith("es")
    ? entry.uninsuredPenaltyText.es
    : entry.uninsuredPenaltyText.en;
  return <>{text}</>;
}
